import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { StatCard, eur } from "./adminUtils";
import { Loader2, TrendingUp, CreditCard, Users, CalendarX } from "lucide-react";

const UNAVAILABLE_LABELS = {
  new_mrr: "New MRR", expansion_mrr: "Expansion MRR", contraction_mrr: "Contraction MRR", churn_mrr: "Churn MRR",
  revenu_encaisse_stripe: "Revenu encaissé Stripe",
};

export default function AdminAnalytics() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-analytics"],
    queryFn: async () => (await api.get("/api/admin/analytics")).data,
    retry: false,
  });

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (isError || !data) return <div className="rounded-2xl border border-dashed border-rose-200 bg-rose-50 py-16 text-center text-sm text-rose-600" data-testid="admin-analytics-error">Impossible de charger les analytics.</div>;

  return (
    <div data-testid="admin-analytics">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">Analytics SaaS</h1>
      <p className="mt-1 text-sm text-slate-500">Métriques d'abonnement fiables, calculées depuis les données réelles</p>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard testId="analytics-mrr" icon={TrendingUp} tone="bg-emerald-50 text-emerald-600" label="MRR estimé" value={eur(data.mrr_eur)} />
        <StatCard testId="analytics-arpu" icon={Users} tone="bg-indigo-50 text-indigo-600" label="ARPU" value={eur(data.arpu_eur)} sub="Revenu moyen par abonnement actif" />
        <StatCard testId="analytics-active" icon={CreditCard} tone="bg-blue-50 text-blue-600" label="Abonnements actifs" value={data.active_subscriptions ?? 0} />
      </div>

      <section className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="admin-analytics-unavailable">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500"><CalendarX className="h-4 w-4" /> Métriques non affichées</h2>
        <p className="mb-3 text-sm text-slate-500">{data.note || "Ces métriques nécessitent un historique suffisant avant d'être fiables."}</p>
        <div className="flex flex-wrap gap-2">
          {(data.unavailable || []).map((k) => (
            <span key={k} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-500" data-testid={`analytics-unavailable-${k}`}>
              {UNAVAILABLE_LABELS[k] || k} — disponible après historique suffisant
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
